import {v4 as uuidv4} from 'uuid';

import {DisplayField} from '@/lib/classes/InfoWindowContent';
import {Comp} from '@/lib/classes/InfoWindowContent/InfoWindowContent';
import {UserAction} from '@/types/commonTypes';
import {
  BaseScenarioDto,
  BuildingBaseScenarioDto,
  CapExRowConfig,
  DEFAULT_CAPEX_ROW_COUNT,
  PortfolioBaseScenarioDto,
  PortfolioBuildingBaseSnapshot,
} from '@/lib/underwriting/core/scenario/types';

export interface BuildingBaseScenarioInput {
  kind: 'building';
  recordId: string;
  recordName: string;
  recordLocation: {latitude: number; longitude: number} | null;
  underwritingData: Record<string, unknown> | undefined;
  comps: Comp[];
  displayFields: DisplayField[];
  whoAndNow: UserAction;
}

export interface PortfolioLinkedBuildingInput {
  id: string;
  name: string;
  location: {latitude: number; longitude: number} | null;
  underwritingData: Record<string, unknown>;
  comps: Comp[];
  displayFields: DisplayField[];
}

export interface PortfolioBaseScenarioInput {
  kind: 'portfolio';
  recordId: string;
  recordName: string;
  recordLocation: {latitude: number; longitude: number} | null;
  underwritingData: Record<string, unknown> | undefined;
  comps: Comp[];
  displayFields: DisplayField[];
  /**
   * Snapshot data for every linked building. The portfolio scenario owns each
   * building's snapshot, CapEx rows, and overrides — buildings linked to a
   * valid portfolio have no separate scenario.
   */
  buildings: PortfolioLinkedBuildingInput[];
  whoAndNow: UserAction;
}

export type BaseScenarioInput = BuildingBaseScenarioInput | PortfolioBaseScenarioInput;

export const createBlankCapExRowConfig = (): CapExRowConfig => ({
  rows: Array.from({length: DEFAULT_CAPEX_ROW_COUNT}, () => ({
    rowId: uuidv4(),
  })),
  deletedRowIds: [],
});

function buildPortfolioBuildingSnapshot(input: PortfolioLinkedBuildingInput): PortfolioBuildingBaseSnapshot {
  return {
    name: input.name,
    location: input.location,
    underwritingData: input.underwritingData,
    comps: input.comps,
    displayFields: input.displayFields,
    capExRows: createBlankCapExRowConfig(),
    overrides: {},
  };
}

function buildBuildingBaseScenario(input: BuildingBaseScenarioInput): BuildingBaseScenarioDto {
  return {
    recordRef: {kind: 'building', id: input.recordId, dataType: 'firestore'},
    scenarioName: 'Base',
    isBase: true,
    overrides: {},
    capExRows: createBlankCapExRowConfig(),
    lastVersionedOverrides: null,
    deleted: null,
    createdOn: input.whoAndNow,
    editedOn: input.whoAndNow,
    recordName: input.recordName,
    recordLocation: input.recordLocation,
    underwritingData: input.underwritingData ?? {},
    comps: input.comps,
    displayFields: input.displayFields,
  };
}

function buildPortfolioBaseScenario(input: PortfolioBaseScenarioInput): PortfolioBaseScenarioDto {
  const buildings: Record<string, PortfolioBuildingBaseSnapshot> = {};
  for (const b of input.buildings) {
    buildings[b.id] = buildPortfolioBuildingSnapshot(b);
  }

  return {
    recordRef: {kind: 'portfolio', id: input.recordId, dataType: 'firestore'},
    scenarioName: 'Base',
    isBase: true,
    overrides: {},
    buildings,
    lastVersionedOverrides: null,
    deleted: null,
    createdOn: input.whoAndNow,
    editedOn: input.whoAndNow,
    recordName: input.recordName,
    recordLocation: input.recordLocation,
    underwritingData: input.underwritingData ?? {},
    comps: input.comps,
    displayFields: input.displayFields,
  };
}

export const buildBaseScenario = (input: BaseScenarioInput): BaseScenarioDto => {
  if (input.kind === 'building') return buildBuildingBaseScenario(input);
  return buildPortfolioBaseScenario(input);
};
